import React, { useState } from 'react';
import { Flag, X, CheckCircle2, AlertTriangle } from 'lucide-react';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reporterUid: string;
  reporterUsername?: string;
  targetType: 'profile_picture' | 'feed_post' | 'feed_comment';
  targetId: string;
  targetUserUid: string;
  targetUsername?: string;
  targetLabel?: string;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  reporterUid,
  reporterUsername = 'Scholar',
  targetType,
  targetId,
  targetUserUid,
  targetUsername,
}) => {
  const [reason, setReason] = useState<
    'inappropriate_image' | 'profanity' | 'abusive' | 'harassment' | 'spam' | 'other'
  >(targetType === 'profile_picture' ? 'inappropriate_image' : 'profanity');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const targetTypeTitle = {
    profile_picture: 'Profile Picture',
    feed_post: 'Community Feed Post',
    feed_comment: 'Community Comment',
  }[targetType];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const res = await fetch('/api/moderation/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reporterUid,
          reporterUsername,
          targetType,
          targetId: String(targetId),
          targetUserUid,
          reason,
          details: details.trim(),
        }),
      });

      if (res.ok) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
          setDetails('');
          onClose();
        }, 2000);
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMessage(data.error || 'Failed to submit report.');
      }
    } catch {
      setErrorMessage('Network error submitting report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs"
    >
      <div className="w-full max-w-md rounded-2xl bg-[#141414] border border-[#262626] p-5 sm:p-6 shadow-2xl space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#262626] pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0">
              <Flag className="w-4 h-4" />
            </div>
            <div>
              <h2
                id="report-modal-title"
                className="text-base font-bold text-[#FAFAFA]"
              >
                Report {targetTypeTitle}
              </h2>
              {targetUsername && (
                <p className="text-xs text-[#737373] text-[#A3A3A3]">
                  User: @{targetUsername}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-[#1C1C1C]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isSuccess ? (
          <div className="py-6 text-center space-y-2">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto animate-bounce" />
            <h3 className="text-sm font-bold text-[#FAFAFA]">
              Report Submitted for Review
            </h3>
            <p className="text-xs text-[#737373] text-[#A3A3A3]">
              Thank you for keeping Quick!Bible safe and aligned with Community Guidelines.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <label className="block text-xs font-semibold text-[#D4D4D4] mb-1">
                Reason for Report
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value as any)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2 text-xs text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              >
                {targetType === 'profile_picture' && (
                  <option value="inappropriate_image">
                    Inappropriate / Sexually Explicit / Offensive Image
                  </option>
                )}
                <option value="profanity">Profanity / Obscene Language</option>
                <option value="abusive">Hate Speech / Abusive Content</option>
                <option value="harassment">Harassment / Bullying / Threats</option>
                <option value="spam">Spam / Commercial Solicitation / Links</option>
                <option value="other">Other Guideline Violation</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="report-details"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1"
              >
                Additional Details (Optional)
              </label>
              <textarea
                id="report-details"
                rows={3}
                placeholder="Explain why this content violates Quick!Bible Community Guidelines..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] p-2.5 text-xs text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              />
            </div>

            {errorMessage && (
              <div
                role="alert"
                className="p-2 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400"
              >
                {errorMessage}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#262626]">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 rounded-xl border border-[#262626] text-xs font-medium text-[#D4D4D4] hover:bg-[#1C1C1C]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-semibold disabled:opacity-50"
              >
                <Flag className="w-3.5 h-3.5" />
                <span>{isSubmitting ? 'Submitting...' : 'Submit Report'}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
